import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { Sparkles, Disc3, Music, User, BadgeCheck, Star } from 'lucide-react';

const ICONS = {
  Sparkles,
  Disc3,
  Music,
  User,
  BadgeCheck,
  Star,
};

/**
 * MarqueeBanner — auto-scrolling horizontal ticker on the Profile page.
 *
 * Renders a set of stat pills (this-week pulls, top genre, top artist,
 * collection size, owned count, want-list size) fetched from
 * ``/api/stats/weekly``. Doubles the item list so we can use a pure CSS
 * translateX animation and get an endless, seamless scroll without a JS
 * loop.
 *
 * Auto-pauses on hover (desktop) so users can actually read a passing stat.
 * When there's only 1 item, renders it static (no marquee). When there's
 * truly zero data (brand-new users), shows a subtle nudge to get started
 * instead of leaving an empty banner. Fetch errors bail silently.
 */
export default function MarqueeBanner({ className = '' }) {
  const [items, setItems] = useState(null); // null = loading, [] = empty
  const [error, setError] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/stats/weekly');
        if (!cancelled) setItems(data?.items || []);
      } catch (e) {
        if (!cancelled) {
          setItems([]); // ensure we never stay in the loading state forever
          setError(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Loading skeleton — thin pill row so layout doesn't jump.
  if (items === null && !error) {
    return (
      <div
        className={`marquee-glass w-full overflow-hidden py-2.5 px-3 ${className}`}
        data-testid="marquee-banner-loading"
        aria-hidden="true"
      >
        <div className="h-4 w-2/3 rounded-full bg-secondary animate-pulse" />
      </div>
    );
  }

  // Zero-data nudge — new accounts with no cards yet. Silently hide on
  // network errors so we don't shove a scary "something went wrong" pill
  // in front of otherwise-working users.
  if (!items || items.length === 0) {
    if (error) return null;
    return (
      <div
        className={`marquee-glass w-full overflow-hidden py-2 px-3 flex items-center gap-2 text-xs text-muted-foreground ${className}`}
        data-testid="marquee-banner-empty"
      >
        <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
        <span>Scan some screenshots to unlock your weekly stats ticker.</span>
      </div>
    );
  }

  // Single-item mode — no marquee animation (pointless with one pill),
  // just render the item centered. Keeps the banner useful for users
  // who only have one qualifying stat (e.g. brand-new week).
  if (items.length === 1) {
    const it = items[0];
    const Icon = ICONS[it.icon] || Sparkles;
    return (
      <div
        className={`marquee-glass w-full overflow-hidden py-2.5 px-3 flex items-center gap-2 text-xs ${className}`}
        data-testid="marquee-banner-single"
      >
        <Icon className="w-3.5 h-3.5 text-[hsl(var(--primary))] shrink-0" />
        <span className="text-muted-foreground uppercase tracking-wider font-mono text-[10px]">
          {it.label}
        </span>
        <span className="font-medium text-foreground">{it.value}</span>
      </div>
    );
  }

  // Duplicate items so the ticker can loop seamlessly. The animation
  // shifts by -50% of the track width — because we've doubled the content,
  // that lands exactly where the original set started.
  const doubled = [...items, ...items];

  return (
    <div
      ref={wrapRef}
      className={`marquee-glass group relative w-full overflow-hidden ${className}`}
      data-testid="marquee-banner"
      aria-label="Your weekly GroovLens stats"
    >
      {/* Left / right fades so items don't clip abruptly at the edges.
          Uses the same tinted glass base so the fades sit ON the glass. */}
      <div className="marquee-fade marquee-fade-left pointer-events-none absolute inset-y-0 left-0 w-10 z-10" />
      <div className="marquee-fade marquee-fade-right pointer-events-none absolute inset-y-0 right-0 w-10 z-10" />

      <div
        className="flex whitespace-nowrap py-2.5 gap-6 will-change-transform animate-marquee group-hover:[animation-play-state:paused] motion-reduce:animate-none"
        style={{ animationDuration: `${Math.max(18, items.length * 5)}s` }}
      >
        {doubled.map((it, idx) => {
          const Icon = ICONS[it.icon] || Sparkles;
          return (
            <div
              key={`${it.key}-${idx}`}
              className="flex items-center gap-2 text-xs shrink-0 px-2 py-0.5"
              data-testid="marquee-banner-item"
            >
              <Icon className="w-3.5 h-3.5 text-[hsl(var(--primary))] shrink-0" />
              <span className="text-muted-foreground uppercase tracking-wider font-mono text-[10px]">
                {it.label}
              </span>
              <span className="font-medium text-foreground">{it.value}</span>
              <span className="text-[hsl(var(--primary))]/40 select-none" aria-hidden="true">•</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
